import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  setDoc,
  updateDoc,
  serverTimestamp,
  type FieldValue,
  type DocumentSnapshot,
} from "firebase/firestore";
import { resilientOnSnapshot } from "../../lib/firebase/resilientSnapshot";
import { withReadTimeout } from "../../lib/firebase/readTimeout";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../../lib/firebase/config";

/**
 * Trilha canônica de páginas/dia — `user_stats/{uid}/daily_pages`, escrita
 * exclusivamente server-side (BUG-18). Única fonte que soma manual + foco;
 * o gráfico semanal do perfil lê daqui. Doc id = dayKey ISO (yyyy-MM-dd,
 * fuso BR), ordenável — busca os últimos `days` por id.
 *
 * BUG-41: era `orderBy(documentId(), "desc") + limit(n)`. Firestore indexa
 * `__name__` **ascendente** automaticamente, mas ordenar `__name__`
 * DESCENDENTE exige índice explícito — que nunca existiu. A query falhava em
 * produção com `failed-precondition: The query requires an index` e o
 * `.catch(() => {})` dos dois consumidores engolia o erro: a trilha voltava
 * vazia e TUDO que depende dela mostrava zero (régua de páginas do hero, card
 * "Sua semana" e o gráfico semanal do perfil). `limitToLast` não resolve — o
 * Firestore o implementa invertendo a query, então cai no mesmo índice.
 *
 * Agora usa **filtro de intervalo** no id, coberto pelo índice ascendente
 * default. De quebra corrige a semântica: o que se quer são os últimos N
 * DIAS, não os últimos N DOCUMENTOS — com dias sem leitura (o caso normal),
 * `limit(8)` podia alcançar semanas atrás e distorcer o gráfico.
 *
 * ⚠️ **Devolve `{pages, minutes}`, não um número — e o dia de FOCO PURO
 * sobrevive.** Até 03/09 esta função filtrava `pages > 0` e descartava o dia
 * inteiro: quem fez 90 min de Foco sem declarar página saía da trilha, e o
 * gráfico do perfil o desenhava como dia vazio enquanto a chama da ofensiva,
 * na mesma tela, o contava. O irmão `fetchDailyTrailForMonth` (a `/streak`)
 * sempre fez o oposto — `pages <= 0 && minutes <= 0` — e a divergência entre
 * dois leitores da MESMA coleção é a doença do BUG-81. Agora a regra de
 * sobrevivência do dia é uma só, e é a de lá.
 *
 * `minutes: null` = campo ausente (dia anterior ao deploy de `focusMinutes`,
 * sem backfill). Distinto de `0`, que é "houve dia, não houve foco" — quem
 * consome tem de manter a distinção, senão afirma que a pessoa não focou.
 */
export async function fetchDailyPagesTrail(
  userId: string,
  days = 7,
): Promise<Record<string, DailyTrailEntry>> {
  const start = new Date();
  start.setDate(start.getDate() - (days - 1));
  const startKey = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}-${String(start.getDate()).padStart(2, "0")}`;

  const q = query(
    collection(db, "user_stats", userId, "daily_pages"),
    where(documentId(), ">=", startKey),
  );
  const snap = await withReadTimeout(getDocs(q));
  const map: Record<string, DailyTrailEntry> = {};
  for (const d of snap.docs) {
    const raw = d.data() as { pages?: number; focusMinutes?: number };
    const pages = typeof raw.pages === "number" && raw.pages > 0 ? raw.pages : 0;
    const minutes = typeof raw.focusMinutes === "number" ? raw.focusMinutes : null;
    // Mesma regra do `fetchDailyTrailForMonth`: doc sem página E sem minuto não
    // descreve dia nenhum. Com minuto, o dia FICA — é o que o gráfico precisa
    // para não desenhar sessão de Foco como dia vazio.
    if (pages <= 0 && (minutes ?? 0) <= 0) continue;
    map[d.id] = { pages, minutes };
  }
  return map;
}

/**
 * Trilha de páginas de UM mês, indexada por dia (1 = índice 0).
 *
 * Existe porque o digest **não guarda a série diária** — ele guarda os
 * agregados. Os gráficos da `/insights` (Tempo lido, Sequência, Ritmo) e o card
 * de Ritmo do Studio precisam do dia a dia, e a única fonte que soma manual +
 * foco é esta trilha. Ler daqui também é o que faz o gráfico existir para meses
 * ANTIGOS: a trilha já está gravada desde 06/2026, então nada depende de um
 * campo novo no cron nem de backfill.
 *
 * Custo: um mês = no máximo 31 docs, e dia sem leitura não gera doc. Só o mês
 * aberto é lido, e o hook guarda o resultado por mês.
 *
 * ⚠️ Intervalo por `documentId()` ascendente (lição do BUG-41): ordenar
 * `__name__` DESCENDENTE exigiria índice explícito que não existe.
 */
/**
 * Um mês inteiro de `daily_pages`, sem interpretar o conteúdo.
 *
 * Ponto único da janela de datas: o fim é EXCLUSIVO no primeiro dia do mês
 * seguinte — comparar com "-31" perderia meses de 30 dias por sorte e fevereiro
 * sempre. E o intervalo é por `documentId()` ASCENDENTE (lição do BUG-41):
 * ordenar `__name__` descendente exigiria índice explícito que não existe.
 */
async function fetchMonthDocs(userId: string, yearMonth: string) {
  const [y, m] = yearMonth.split("-").map(Number);
  if (!y || !m) return null;
  const nextY = m === 12 ? y + 1 : y;
  const nextM = m === 12 ? 1 : m + 1;
  const endKey = `${nextY}-${String(nextM).padStart(2, "0")}-01`;

  const snap = await getDocs(
    query(
      collection(db, "user_stats", userId, "daily_pages"),
      where(documentId(), ">=", `${yearMonth}-01`),
      where(documentId(), "<", endKey),
    ),
  );
  return { snap, daysInMonth: new Date(y, m, 0).getDate() };
}

export async function fetchDailyPagesForMonth(
  userId: string,
  yearMonth: string,
): Promise<number[]> {
  const res = await fetchMonthDocs(userId, yearMonth);
  if (!res) return [];

  const trail = new Array<number>(res.daysInMonth).fill(0);
  for (const d of res.snap.docs) {
    const day = Number(d.id.slice(8, 10));
    const pages = (d.data() as { pages?: number }).pages;
    if (day >= 1 && day <= res.daysInMonth && typeof pages === "number" && pages > 0) {
      trail[day - 1] = pages;
    }
  }
  return trail;
}

/**
 * Um INTERVALO arbitrário da trilha, em páginas por dia — a fonte da variante
 * "12 semanas" do card de Ritmo.
 *
 * Difere das duas leituras vizinhas pelo que ANCORA a janela:
 * `fetchDailyPagesTrail` ancora em *hoje* e `fetchDailyPagesForMonth` no mês
 * civil. A variante de 12 semanas precisa de nenhum dos dois: ela é ancorada no
 * fim do mês que o usuário está OLHANDO, senão o card falaria de uma janela que
 * a página sob ele não descreve (abrir julho e receber as 12 semanas até hoje).
 *
 * ⚠️ Intervalo por `documentId()` ASCENDENTE, como as outras duas — ordenar
 * `__name__` descendente exigiria índice explícito que não existe (BUG-41), e a
 * falha seria muda: os dois consumidores da época engoliam o erro e o gráfico
 * saía zerado.
 *
 * Custo: 84 docs no pior caso, e dia sem leitura não gera doc.
 *
 * @param startKey dayKey ISO inclusivo ("YYYY-MM-DD", fuso BR).
 * @param endKey   dayKey ISO **inclusivo**.
 */
export async function fetchDailyPagesRange(
  userId: string,
  startKey: string,
  endKey: string,
): Promise<Record<string, number>> {
  const snap = await getDocs(
    query(
      collection(db, "user_stats", userId, "daily_pages"),
      where(documentId(), ">=", startKey),
      where(documentId(), "<=", endKey),
    ),
  );
  const map: Record<string, number> = {};
  for (const d of snap.docs) {
    const pages = (d.data() as { pages?: number }).pages;
    if (typeof pages === "number" && pages > 0) map[d.id] = pages;
  }
  return map;
}

/** Um dia da trilha, indexado por dayKey. `minutes: null` = campo ausente. */
export interface DailyTrailEntry {
  pages: number;
  minutes: number | null;
}

/**
 * O mês inteiro indexado por dayKey, com páginas E minutos de foco — a fonte do
 * calendário de intensidade (`/streak`).
 *
 * Difere de `fetchDailyPagesForMonth` (que devolve só o vetor de páginas para
 * os gráficos da `/insights`) em duas coisas que o calendário precisa:
 *
 *  1. **a chave**, porque a célula liga o dia a uma data real, não a um índice;
 *  2. **`focusMinutes`**, sem o qual dia de foco sem página declarada sairia
 *     apagado enquanto a ofensiva o conta.
 *
 * ⚠️ `minutes` é `null` — e não `0` — quando o campo não existe no doc. Dias
 * anteriores ao deploy de `focusMinutes` não têm medição, e a folha do dia diz
 * "sem medição" em vez de afirmar que a pessoa não focou. Não há backfill.
 */
export async function fetchDailyTrailForMonth(
  userId: string,
  yearMonth: string,
): Promise<Record<string, DailyTrailEntry>> {
  const res = await fetchMonthDocs(userId, yearMonth);
  if (!res) return {};

  const trail: Record<string, DailyTrailEntry> = {};
  for (const d of res.snap.docs) {
    const raw = d.data() as { pages?: number; focusMinutes?: number };
    const pages = typeof raw.pages === "number" && raw.pages > 0 ? raw.pages : 0;
    const minutes = typeof raw.focusMinutes === "number" ? raw.focusMinutes : null;
    // Doc sem página e sem minuto não descreve dia nenhum — não ocupa a trilha.
    if (pages <= 0 && (minutes ?? 0) <= 0) continue;
    trail[d.id] = { pages, minutes };
  }
  return trail;
}

/**
 * Contribuição de um gênero/autor no mês (camada PRO).
 *
 * `books` = livros CONCLUÍDOS. `minutes`/`pages` = sessões de Foco — e por isso
 * são zero para quem não usa Foco, que é a maioria (p75 = 0 min, medição de
 * 03/08). A UI tem de saber viver com `minutes === 0` sem inventar ritmo.
 */
export interface StatsLayerGenre {
  slug: string;
  label: string;
  books: number;
  minutes: number;
  pages: number;
}

export interface StatsLayerAuthor {
  name: string;
  books: number;
  minutes: number;
  pages: number;
}

/** Um livro concluído no mês, como o cron grava no digest. */
export interface MonthlyDigestBook {
  canonicalId: string;
  title: string;
  cover: string;
  // ── digest v2 (26/09, docs/systems/stats-community.md §2) ── ausente = não há o dado
  workId?: string;
  /** Nota da estante, 0,5–5. */
  rating?: number;
  pages?: number;
  /** Dias do início ao término — só com `startedAt` (gravado desde 15/09). */
  days?: number;
  /** Média da comunidade (da obra quando existe). */
  communityAvg?: number;
  communityRatings?: number;
  /** Leitores no Livra (da obra quando existe) — o "livro raro". */
  readerCount?: number;
}

/**
 * Fatos v2 do mês (26/09). `null` no digest = mês v1 (`layersVersion < 2`):
 * o campo NÃO existe, e a tela não pode afirmar "0 dias lendo" sobre um mês em
 * que a medição ainda não existia.
 */
export interface MonthlyDigestV2 {
  /** Dias com página > 0 ou Foco de 1 min ou mais. ≠ `streakDays`. */
  readingDays: number;
  readingDayMask: number;
  /** Maior sequência de dias seguidos DENTRO do mês. */
  monthStreakBest: number;
  bestDay: { dayKey: string; pages: number } | null;
  longestSessionMin: number;
  /** Fração do Foco entre 22h e 5h; `null` sem Foco. */
  nightShare: number | null;
  ppmSessions: number;
}

/**
 * `user_stats/{uid}/digests/{YYYY-MM}` — fechado pelo cron
 * `computeMonthlyDigest` no dia 1º às 04h (SP). Schema em
 * `.claude/skills/stats-hub/SKILL.md`.
 *
 * ⚠️ Normalizado na leitura (ver `normalizeDigest`): digests de meses antigos
 * nasceram sem `pagesSource`, e a página fazia `digest.hourHistogram.some(...)`
 * direto no dado cru — um campo faltando derrubava a tela inteira.
 */
export interface MonthlyDigest {
  yearMonth: string;
  minutesRead: number;
  pagesRead: number;
  /** "daily_trail" = toda página (focus+manual) · "focus_only" = legado. */
  pagesSource: "daily_trail" | "focus_only";
  booksFinishedCount: number;
  booksFinished: MonthlyDigestBook[];
  /** Recorde VITALÍCIO da conta (`user_stats.longestStreak`), não do mês. */
  streakMax: number;
  streakDays: number;
  topAuthor: { name: string; books: number; minutes: number } | null;
  topGenres: Array<{ slug: string; label: string; books: number }>;
  /**
   * Camadas PRO — `docs/systems/stats-pro-layers.md`. Escritas pelo cron desde
   * 18/08; mês fechado antes disso não as tem.
   *
   * ⚠️ `topGenres` (free, top 3, só `books`) e `byGenre` (PRO, top 5, com tempo)
   * são campos SEPARADOS de propósito. Fundir faria o campo free carregar dado
   * pago e reduziria o gate a um `slice()` na UI.
   */
  byGenre: StatsLayerGenre[];
  byAuthor: StatsLayerAuthor[];
  /** Fração [0..1] dos minutos de Foco atribuídos a um livro. */
  focusAttributionCoverage: number;
  /**
   * `0` = mês anterior à medição das camadas.
   *
   * Sem este campo, `byGenre` vazio significaria ao mesmo tempo "mês antigo" e
   * "você não leu gênero nenhum" — e a tela afirmaria a segunda coisa. É a
   * mesma distinção que a Ofensiva faz entre `focusMinutes` ausente e zero.
   */
  layersVersion: number;
  /** 24 baldes de minutos por hora (fuso BR). */
  hourHistogram: number[];
  peakHour: number;
  avgPpm: number;
  pacePctOfGoal: number;
  /**
   * `computedAt` do digest em ms — a comparação com o Livra só vale se foi
   * calculada sobre ESTE digest (`compare.sourceComputedAtMs`); recalculou o
   * mês, a manchete velha some (stats-community.md §3.6).
   */
  computedAtMs?: number | null;
  /** Fatos v2; `null` = mês v1. */
  v2?: MonthlyDigestV2 | null;
}

function normalizeDigest(yearMonth: string, raw: Record<string, unknown>): MonthlyDigest {
  const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);
  const books = Array.isArray(raw.booksFinished) ? (raw.booksFinished as MonthlyDigestBook[]) : [];
  const optNum = (v: unknown): number | undefined =>
    typeof v === "number" && Number.isFinite(v) ? v : undefined;
  const isV2 = num(raw.layersVersion) >= 2;
  const computedAt = raw.computedAt as { toMillis?: () => number } | undefined;
  const hours = Array.isArray(raw.hourHistogram) ? (raw.hourHistogram as number[]).map(num) : [];
  const topAuthorRaw = raw.topAuthor as MonthlyDigest["topAuthor"];

  return {
    yearMonth: typeof raw.yearMonth === "string" ? raw.yearMonth : yearMonth,
    minutesRead: num(raw.minutesRead),
    pagesRead: num(raw.pagesRead),
    pagesSource: raw.pagesSource === "daily_trail" ? "daily_trail" : "focus_only",
    booksFinishedCount: num(raw.booksFinishedCount),
    booksFinished: books.map((b) => {
      const out: MonthlyDigestBook = {
        canonicalId: typeof b?.canonicalId === "string" ? b.canonicalId : "",
        title: typeof b?.title === "string" ? b.title : "",
        cover: typeof b?.cover === "string" ? b.cover : "",
      };
      // Só as chaves que existem: "sem nota" e "nota 0" contam histórias diferentes.
      if (typeof b?.workId === "string" && b.workId) out.workId = b.workId;
      for (const k of ["rating", "pages", "days", "communityAvg", "communityRatings", "readerCount"] as const) {
        const v = optNum(b?.[k]);
        if (v !== undefined) out[k] = v;
      }
      return out;
    }),
    streakMax: num(raw.streakMax),
    streakDays: num(raw.streakDays),
    topAuthor: topAuthorRaw && typeof topAuthorRaw.name === "string" && topAuthorRaw.name
      ? {
          name: topAuthorRaw.name,
          books: num(topAuthorRaw.books),
          minutes: num(topAuthorRaw.minutes),
        }
      : null,
    topGenres: Array.isArray(raw.topGenres)
      ? (raw.topGenres as MonthlyDigest["topGenres"]).filter((g) => typeof g?.label === "string")
      : [],
    // 24 baldes sempre: com array curto/ausente o índice de `peakHour` cairia
    // em `undefined` e o gráfico do card sairia com buracos.
    hourHistogram: Array.from({ length: 24 }, (_, i) => hours[i] ?? 0),
    peakHour: Math.min(23, Math.max(0, Math.round(num(raw.peakHour)))),
    avgPpm: num(raw.avgPpm),
    pacePctOfGoal: num(raw.pacePctOfGoal),
    byGenre: normalizeLayer(raw.byGenre, (g) => ({
      slug: typeof g.slug === "string" ? g.slug : "",
      label: typeof g.label === "string" ? g.label : "",
      books: num(g.books),
      minutes: num(g.minutes),
      pages: num(g.pages),
    })).filter((g) => g.label !== ""),
    byAuthor: normalizeLayer(raw.byAuthor, (a) => ({
      name: typeof a.name === "string" ? a.name : "",
      books: num(a.books),
      minutes: num(a.minutes),
      pages: num(a.pages),
    })).filter((a) => a.name !== ""),
    // Ausente ⇒ 1: mês antigo não tem cobertura medida, e 0 mandaria a tela
    // acusar "atribuição insuficiente" num mês em que nada foi atribuído
    // porque a medição ainda não existia. Quem responde por esse caso é
    // `layersVersion`, não a cobertura.
    focusAttributionCoverage:
      typeof raw.focusAttributionCoverage === "number" &&
      Number.isFinite(raw.focusAttributionCoverage)
        ? raw.focusAttributionCoverage
        : 1,
    layersVersion: num(raw.layersVersion),
    computedAtMs: typeof computedAt?.toMillis === "function" ? computedAt.toMillis() : null,
    v2: isV2
      ? {
          readingDays: num(raw.readingDays),
          readingDayMask: num(raw.readingDayMask),
          monthStreakBest: num(raw.monthStreakBest),
          bestDay:
            raw.bestDay && typeof (raw.bestDay as { dayKey?: unknown }).dayKey === "string"
              ? {
                  dayKey: (raw.bestDay as { dayKey: string }).dayKey,
                  pages: num((raw.bestDay as { pages?: unknown }).pages),
                }
              : null,
          longestSessionMin: num(raw.longestSessionMin),
          nightShare: typeof raw.nightShare === "number" ? raw.nightShare : null,
          ppmSessions: num(raw.ppmSessions),
        }
      : null,
  };
}

/** Lista de camada vinda do doc, sem confiar em forma nem em tipo de item. */
function normalizeLayer<T>(
  raw: unknown,
  map: (item: Record<string, unknown>) => T,
): T[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((item): item is Record<string, unknown> => !!item && typeof item === "object")
    .map(map);
}

/** Um mês fechado. `null` = o cron nunca escreveu esse mês para este usuário. */
export async function fetchMonthlyDigest(
  userId: string,
  yearMonth: string,
): Promise<MonthlyDigest | null> {
  const snap = await getDoc(doc(db, "user_stats", userId, "digests", yearMonth));
  if (!snap.exists()) return null;
  return normalizeDigest(yearMonth, snap.data() as Record<string, unknown>);
}

/**
 * Meses que EXISTEM, do mais recente para o mais antigo.
 *
 * A tela antiga oferecia os últimos 12 meses fixos e deixava o usuário cair em
 * "sem dados" a cada toque — inclusive em meses anteriores à conta existir.
 * Aqui o seletor só mostra o que o cron fechou, e a lacuna vira um traço
 * explicado em vez de um mês clicável vazio.
 *
 * Janela de 24 meses e ordenação em memória: `orderBy(documentId(), "desc")`
 * pediria índice que não existe (BUG-41), e são no máximo 24 docs de um id só.
 */
export async function listDigestMonths(userId: string, months = 24): Promise<string[]> {
  const now = new Date();
  const cutoff = new Date(now.getFullYear(), now.getMonth() - (months - 1), 1);
  const cutoffKey = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, "0")}`;

  const snap = await getDocs(
    query(
      collection(db, "user_stats", userId, "digests"),
      where(documentId(), ">=", cutoffKey),
    ),
  );
  // ⚠️ Filtro de FORMATO (26/09, stats-community.md §1.1): o range por
  // documentId aceita qualquer id >= cutoff — "2026" passaria e viraria chip
  // de mês. O Wrap mora em `year_digests` justamente por isso; o filtro é a
  // defesa em profundidade para qualquer doc estranho na subcoleção.
  return snap.docs
    .map((d) => d.id)
    .filter((id) => DIGEST_MONTH_ID.test(id))
    .sort((a, b) => b.localeCompare(a));
}

const DIGEST_MONTH_ID = /^\d{4}-(0[1-9]|1[0-2])$/;

export interface RecomputeMonthlyDigestResult {
  yearMonth: string;
  /** `false` = mês sem digest e sem conteúdo; nada foi gravado. */
  written: boolean;
  booksFinishedCount: number;
}

/**
 * Refaz o digest de um mês FECHADO — espelho de
 * `functions/src/stats/recomputeMonthlyDigest.ts`. Spec:
 * `docs/systems/monthly-digest-recompute.md`.
 *
 * Existe por causa do BUG-101: o cron congela o mês (`computedAt`), então
 * corrigir a data de conclusão de um livro nunca alcançava a `/insights`.
 *
 * ⚠️ O servidor recusa o mês corrente (`failed-precondition`) — recalcular o mês
 * em andamento gravaria um digest parcial que o cron pularia no dia 1º.
 */
export async function recomputeMonthlyDigest(
  yearMonth: string,
): Promise<RecomputeMonthlyDigestResult> {
  const region = import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION as string | undefined;
  const app = getApp();
  const fns =
    region && region.trim().length > 0 ? getFunctions(app, region.trim()) : getFunctions(app);
  const call = httpsCallable<{ yearMonth: string }, RecomputeMonthlyDigestResult>(
    fns,
    "recomputeMonthlyDigest",
  );
  const res = await call({ yearMonth });
  return res.data;
}

export interface UserWallet {
  coins: number;
}

export interface UserStats {
  /** Livros únicos concluídos (não inclui releituras). */
  totalBooksRead: number;
  /** Total de conclusões incluindo releituras. Fallback para totalBooksRead em docs antigos. */
  totalReads: number;
  totalPagesRead: number;
  currentStreak: number;
  /** Maior sequência de leitura alguma vez atingida. */
  longestStreak: number;
  /**
   * Último dia (chave "YYYY-MM-DD" em BR) com leitura registrada. Vem do MESMO
   * documento que `currentStreak` — é o que permite validar a janela da streak
   * sem depender do doc `users` chegar junto. Nunca exiba `currentStreak` sem
   * passar por `computeEffectiveStreak(lastReadDate, currentStreak)` (BUG-47).
   */
  lastReadDate?: string;
  /**
   * Resenhas escritas na vida. Mantido pelo trigger `onBookReviewWritten`
   * (+1 no create, −1 no delete) — nasceu em 31/08 com as badges de resenha,
   * e antes dele o produto não sabia esse número em lugar nenhum.
   *
   * ⚠️ Sem backfill: resenha anterior à leva não conta.
   */
  totalReviews: number;
  /** Resenhas com TEXTO (≥ 20 caracteres) — `onBookReviewWritten` (24/09). */
  totalWrittenReviews: number;
  /** Livros em Quero ler agora — `onUserBookWritten` (24/09, Tsundoku). */
  wantToReadCount: number;
  /**
   * Gêneros distintos entre os livros concluídos — derivado de
   * `user_stats.genresRead` (item 12b F6, barra do Eclético). Guarda-chuva
   * que não é gênero (`ficcao`) não conta, mesma régua do servidor
   * (`NON_GENRE_CATEGORIES`, paridade em `completionBadges.test.ts`).
   */
  genresReadCount?: number;
  /**
   * Obras independentes distintas concluídas — derivado de
   * `user_stats.indieWorksRead` (ids de `book_works`, escrito só pelo
   * `onUserBookWritten`; barra do Garimpo, D47).
   */
  indieWorksReadCount?: number;
  /**
   * Minutos de foco RECOMPENSADOS acumulados. Mesma unidade de
   * `daily_pages.focusMinutes` — o que o servidor pagou, já com o teto diário
   * e o corte de overrun aplicados —, então o vitalício bate com a soma que o
   * calendário de /streak mostra.
   */
  totalFocusMin: number;
  /** Recorde de páginas numa semana (`closeWeeklyLeagues`). Ausente = 0. Aba Sempre (26/09). */
  bestWeekPagesEver?: number;
  wallet: UserWallet;
  updatedAt: string;
}

interface UserStatsDoc {
  totalBooksRead?: number;
  totalReads?: number;
  totalPagesRead?: number;
  currentStreak?: number;
  longestStreak?: number;
  lastReadDate?: string;
  totalReviews?: number;
  totalWrittenReviews?: number;
  wantToReadCount?: number;
  genresRead?: unknown;
  indieWorksRead?: unknown;
  totalFocusMin?: number;
  bestWeekPagesEver?: number;
  wallet?: {
    coins?: number;
  };
  updatedAt?: string | FieldValue;
}

/** Espelho de `NON_GENRE_CATEGORIES` (`functions/src/books/completionBadges.ts`). */
export const NON_GENRE_CATEGORIES_CLIENT: readonly string[] = ["ficcao", "literatura"];

function countGenres(raw: unknown): number {
  if (!Array.isArray(raw)) return 0;
  const skip = new Set(NON_GENRE_CATEGORIES_CLIENT);
  return new Set(raw.filter((g): g is string => typeof g === "string" && !skip.has(g))).size;
}

/** Ids distintos de obra (strings não vazias) — régua do `garimpoBadgesFor` do servidor. */
function countDistinctIds(raw: unknown): number {
  if (!Array.isArray(raw)) return 0;
  return new Set(raw.filter((id): id is string => typeof id === "string" && id.length > 0)).size;
}

function nowIso(): string {
  return new Date().toISOString();
}

function normalizeStats(raw: UserStatsDoc | null | undefined): UserStats {
  const walletCoinsRaw = raw?.wallet?.coins;
  const uniqueBooks = typeof raw?.totalBooksRead === "number" ? raw.totalBooksRead : 0;
  return {
    totalBooksRead: uniqueBooks,
    totalReads: typeof raw?.totalReads === "number" ? raw.totalReads : uniqueBooks,
    totalPagesRead: typeof raw?.totalPagesRead === "number" ? raw.totalPagesRead : 0,
    currentStreak: typeof raw?.currentStreak === "number" ? raw.currentStreak : 0,
    longestStreak: typeof raw?.longestStreak === "number" ? raw.longestStreak : 0,
    lastReadDate: typeof raw?.lastReadDate === "string" ? raw.lastReadDate : undefined,
    totalReviews: typeof raw?.totalReviews === "number" ? raw.totalReviews : 0,
    totalWrittenReviews: typeof raw?.totalWrittenReviews === "number" ? raw.totalWrittenReviews : 0,
    wantToReadCount: typeof raw?.wantToReadCount === "number" ? raw.wantToReadCount : 0,
    genresReadCount: countGenres(raw?.genresRead),
    indieWorksReadCount: countDistinctIds(raw?.indieWorksRead),
    totalFocusMin: typeof raw?.totalFocusMin === "number" ? raw.totalFocusMin : 0,
    bestWeekPagesEver: typeof raw?.bestWeekPagesEver === "number" ? raw.bestWeekPagesEver : 0,
    wallet: {
      coins: typeof walletCoinsRaw === "number" ? walletCoinsRaw : 0,
    },
    updatedAt: typeof raw?.updatedAt === "string" ? raw.updatedAt : nowIso(),
  };
}

export function emptyUserStats(): UserStats {
  return normalizeStats(null);
}

export async function createUserStatsIfMissing(uid: string): Promise<void> {
  const ref = doc(db, "user_stats", uid);
  const snap = await getDoc(ref);
  if (snap.exists()) return;
  await setDoc(ref, {
    totalBooksRead: 0,
    totalPagesRead: 0,
    currentStreak: 0,
    longestStreak: 0,
    wallet: {
      coins: 0,
    },
    updatedAt: nowIso(),
  });
}

export async function getUserStats(uid: string): Promise<UserStats> {
  const snap = await getDoc(doc(db, "user_stats", uid));
  if (!snap.exists()) return emptyUserStats();
  return normalizeStats(snap.data() as UserStatsDoc);
}

export function subscribeToUserStats(
  uid: string,
  callback: (stats: UserStats) => void,
  onError?: (error: Error) => void,
): () => void {
  return resilientOnSnapshot(
    (next, error) => onSnapshot(doc(db, "user_stats", uid), next, error),
    (snap: DocumentSnapshot) => callback(snap.exists() ? normalizeStats(snap.data() as UserStatsDoc) : emptyUserStats()),
    (err) => onError?.(err),
  );
}


// ── Comparação com os leitores do Livra (Estatísticas v2, 26/09) ─────────────
// Escrita pelo cron `computeMonthlyCommunity` (docs/systems/stats-community.md
// §3.4). `headline` é free; `pro` a rule só deixa PRO ler.

export type CompareHeadline =
  | { axis: "pages" | "books" | "reading_days" | "streak"; pct: number; value: number }
  | { axis: "genre"; pct: number; value: number; slug: string; label: string; readers: number }
  | { axis: "participant" };

export interface CompareBase {
  period: string;
  kind: "month" | "year";
  /** O `computedAt` do digest sobre o qual a comparação foi feita. */
  sourceComputedAtMs: number;
  activeCount: number;
}

export interface CompareHeadlineDoc extends CompareBase {
  headline: CompareHeadline;
}

export interface CompareProDoc extends CompareBase {
  pages: { pct: number; value: number; median: number };
  books: { pct: number; value: number; median: number };
  minutes: { pct: number; value: number; median: number };
  genres: Array<{ slug: string; label: string; topPct: number; value: number; readers: number }>;
  pace: { userPpm: number; medianPpm: number; n: number } | null;
  ratings: {
    compared: number;
    above: number;
    below: number;
    equal: number;
    meanDiff: number;
    boldest: {
      canonicalId: string;
      userRating: number;
      communityAvg: number;
      communityRatings: number;
      diff: number;
    } | null;
  } | null;
  rareBooks: Array<{ canonicalId: string; readerCount: number }>;
  night: { userShare: number; medianShare: number; n: number } | null;
}

/**
 * Um doc de comparação de um período. `null` = não existe (mês abaixo do piso
 * de 30 leitores, ou anterior à feature) — a tela simplesmente não mostra.
 *
 * ⚠️ `which: "pro"` SÓ para quem é PRO: a rule nega o free, e pedir mesmo
 * assim transformaria cada abertura da tela num `permission-denied` capturado.
 */
export async function fetchCompare<W extends "headline" | "pro">(
  userId: string,
  period: string,
  which: W,
  kind: "month" | "year" = "month",
): Promise<(W extends "headline" ? CompareHeadlineDoc : CompareProDoc) | null> {
  const group = kind === "month" ? "digests" : "year_digests";
  const snap = await getDoc(doc(db, "user_stats", userId, group, period, "compare", which));
  if (!snap.exists()) return null;
  const raw = snap.data() as Record<string, unknown>;
  if (typeof raw.sourceComputedAtMs !== "number") return null;
  return raw as unknown as W extends "headline" ? CompareHeadlineDoc : CompareProDoc;
}

/**
 * A pessoa VIU a retrospectiva do mês (Estatísticas v2, 26/09). O card da Home
 * some em todo aparelho — por isso o marcador é do servidor, não do aparelho.
 * Rules: `recapSeenMonth` na whitelist do dono, com o formato "YYYY-MM".
 */
export async function markRecapSeen(userId: string, yearMonth: string): Promise<void> {
  await updateDoc(doc(db, "users", userId), { recapSeenMonth: yearMonth, updatedAt: serverTimestamp() });
}

// ── Wrap anual (Estatísticas v2, fatia 5) ────────────────────────────────────
// `user_stats/{uid}/year_digests/{YYYY}` — escrito pelo cron `computeYearlyWrap`
// em 15/12 (docs/systems/stats-community.md §4.3). Dados até 14/12.

export interface YearWrapBook extends MonthlyDigestBook {
  finishedAtMs: number | null;
}

export interface YearWrap {
  year: string;
  /** "YYYY-12-14" — a tela diz "de 1º/01 a 14/12". */
  throughDayKey: string;
  computedAtMs: number | null;
  booksFinishedCount: number;
  booksByMonth: number[];
  books: YearWrapBook[];
  top5: Array<{ canonicalId: string; title: string; cover: string; rating: number }>;
  strongestMonth: { month: string; books: number; pages: number } | null;
  longestBook: { canonicalId: string; title: string; cover: string; pages: number } | null;
  fastestBook: { canonicalId: string; title: string; cover: string; days: number; pages: number } | null;
  topGenre: { slug: string; label: string; books: number } | null;
  topAuthor: { name: string; books: number } | null;
  genres: Array<{ slug: string; label: string; books: number }>;
  pagesRead: number;
  minutesRead: number;
  /** Algum mês com páginas só do Foco (antes da trilha, 11/06/2026). */
  pagesSourceMixed: boolean;
  hourHistogram: number[];
  nightShare: number | null;
  readingDays: number;
  yearStreakBest: number;
  /** 1º dia com dado de dia; `null` = nenhum. A tela ressalva. */
  dayDataFrom: string | null;
  readingGoal: number | null;
}

function normalizeYearWrap(year: string, raw: Record<string, unknown>): YearWrap {
  const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);
  const str = (v: unknown): string => (typeof v === "string" ? v : "");
  const ms = (v: unknown): number | null => {
    const t = v as { toMillis?: () => number } | undefined;
    return typeof t?.toMillis === "function" ? t.toMillis() : null;
  };
  const obj = (v: unknown): Record<string, unknown> | null =>
    v && typeof v === "object" ? (v as Record<string, unknown>) : null;
  const hours = Array.isArray(raw.hourHistogram) ? (raw.hourHistogram as unknown[]).map(num) : [];
  const monthsRaw = Array.isArray(raw.booksByMonth) ? (raw.booksByMonth as unknown[]).map(num) : [];
  const bookRef = (v: unknown) => {
    const b = obj(v);
    return b && str(b.title) ? { canonicalId: str(b.canonicalId), title: str(b.title), cover: str(b.cover) } : null;
  };
  const strongest = obj(raw.strongestMonth);
  const longest = bookRef(raw.longestBook);
  const fastest = bookRef(raw.fastestBook);
  const genre = obj(raw.topGenre);
  const author = obj(raw.topAuthor);
  const list = (v: unknown) => (Array.isArray(v) ? v.map(obj).filter((x): x is Record<string, unknown> => x !== null) : []);

  return {
    year: str(raw.year) || year,
    throughDayKey: str(raw.throughDayKey) || `${year}-12-14`,
    computedAtMs: ms(raw.computedAt),
    booksFinishedCount: num(raw.booksFinishedCount),
    booksByMonth: Array.from({ length: 12 }, (_, i) => monthsRaw[i] ?? 0),
    books: list(raw.books).map((b) => {
      const out: YearWrapBook = { canonicalId: str(b.canonicalId), title: str(b.title), cover: str(b.cover), finishedAtMs: ms(b.finishedAt) };
      if (str(b.workId)) out.workId = str(b.workId);
      for (const k of ["rating", "pages", "days", "communityAvg", "communityRatings", "readerCount"] as const) {
        if (typeof b[k] === "number" && Number.isFinite(b[k])) out[k] = b[k] as number;
      }
      return out;
    }),
    top5: list(raw.top5)
      .filter((b) => str(b.title) && typeof b.rating === "number")
      .map((b) => ({ canonicalId: str(b.canonicalId), title: str(b.title), cover: str(b.cover), rating: num(b.rating) })),
    strongestMonth: strongest && str(strongest.month)
      ? { month: str(strongest.month), books: num(strongest.books), pages: num(strongest.pages) }
      : null,
    longestBook: longest ? { ...longest, pages: num(obj(raw.longestBook)?.pages) } : null,
    fastestBook: fastest
      ? { ...fastest, days: num(obj(raw.fastestBook)?.days), pages: num(obj(raw.fastestBook)?.pages) }
      : null,
    topGenre: genre && str(genre.label) ? { slug: str(genre.slug), label: str(genre.label), books: num(genre.books) } : null,
    topAuthor: author && str(author.name) ? { name: str(author.name), books: num(author.books) } : null,
    genres: list(raw.genres)
      .filter((g) => str(g.label))
      .map((g) => ({ slug: str(g.slug), label: str(g.label), books: num(g.books) })),
    pagesRead: num(raw.pagesRead),
    minutesRead: num(raw.minutesRead),
    pagesSourceMixed: raw.pagesSourceMixed === true,
    hourHistogram: Array.from({ length: 24 }, (_, i) => hours[i] ?? 0),
    nightShare: typeof raw.nightShare === "number" ? raw.nightShare : null,
    readingDays: num(raw.readingDays),
    yearStreakBest: num(raw.yearStreakBest),
    dayDataFrom: str(raw.dayDataFrom) || null,
    readingGoal: typeof raw.readingGoal === "number" && raw.readingGoal > 0 ? raw.readingGoal : null,
  };
}

/** O Wrap de um ano. `null` = ainda não calculado (antes de 15/12) ou ano sem leitura. */
export async function fetchYearWrap(userId: string, year: string): Promise<YearWrap | null> {
  const snap = await getDoc(doc(db, "user_stats", userId, "year_digests", year));
  if (!snap.exists()) return null;
  return normalizeYearWrap(year, snap.data() as Record<string, unknown>);
}

/** A pessoa VIU o Wrap do ano — o card da Home some em todo aparelho (`wrapSeenYear`). */
export async function markWrapSeen(userId: string, year: string): Promise<void> {
  await updateDoc(doc(db, "users", userId), { wrapSeenYear: year, updatedAt: serverTimestamp() });
}
